import React, { ReactNode } from "react";
import { Scoped } from "kremling";

import { CpSelect } from "../cp-select/cp-select.component";
import {
  defaultSearchFilter,
  filterAlpha,
  SelectDataItem,
} from "../cp-select/common/utils";
import styles from "./../cp-select/cp-select.styles.scss";

export type CpSelectSingleProps<TData, TValue = TData> = {
  allowTextWrap?: boolean;
  appendTo?: HTMLElement | "parent";
  autoSelectOnSearch?: boolean;
  className?: string;
  clearable?: boolean;
  contentWidth?: number | "sm" | "md" | "lg" | "block" | "auto";
  data?: TData[];
  disabled?: boolean;
  disableSelection?: boolean;
  emptyStateAction?: (params: {
    onChange: (value: TValue) => void;
    close: () => void;
  }) => void;
  error?: boolean | string;
  groupType?: "header" | "border";
  isGroupData?: boolean;
  loading?: boolean;
  onBlur?: () => void;
  onChange: (value: NoInfer<TValue> | null) => void;
  onClose?: () => void;
  onOpen?: () => void;
  placeholder?: string;
  position?:
    | "auto"
    | "top"
    | "top-start"
    | "top-end"
    | "right"
    | "right-start"
    | "right-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "left"
    | "left-start"
    | "left-end";
  preventKeyOpen?: boolean;
  preventOutsideClickUntilClosed?: boolean;
  renderEmptyState?: (params: any) => ReactNode;
  renderFooter?: (params: any) => ReactNode;
  renderTrigger?: (props: any) => ReactNode;
  searchFilter?: boolean | ((item: any) => boolean);
  searchOnChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchValue?: string;
  stopPropagation?: boolean;
  style?: React.CSSProperties;
  title?: string;
  transformData?: (item: TValue) => SelectDataItem;
  triggerIsBlock?: boolean;
  triggerRef?: React.RefObject<any>;
  value?: TValue | null;
};

function CpSelectSingleBase<TData, TValue = TData>({
  allowTextWrap,
  appendTo,
  autoSelectOnSearch,
  className,
  clearable,
  contentWidth,
  data,
  disabled,
  disableSelection,
  emptyStateAction,
  error,
  groupType,
  isGroupData,
  loading,
  onBlur,
  onChange,
  onClose,
  onOpen,
  placeholder = "Select",
  position,
  preventKeyOpen,
  preventOutsideClickUntilClosed,
  renderEmptyState,
  renderFooter,
  renderTrigger,
  searchFilter,
  searchOnChange,
  searchPlaceholder,
  searchValue,
  stopPropagation,
  style,
  title,
  transformData,
  triggerIsBlock,
  triggerRef,
  value,
}: CpSelectSingleProps<TData, TValue>) {
  return (
    <Scoped css={styles}>
      <CpSelect<TData, TValue>
        allowTextWrap={allowTextWrap}
        appendTo={appendTo}
        autoSelectOnSearch={autoSelectOnSearch}
        className={className}
        clearable={clearable}
        contentWidth={contentWidth}
        data={data}
        disabled={disabled}
        disableSelection={disableSelection}
        emptyStateAction={emptyStateAction}
        insertSearch={!!searchOnChange}
        isGroupData={isGroupData}
        groupType={groupType}
        position={position}
        loading={loading}
        onBlur={onBlur}
        onChange={onChange as (value: TValue | TValue[]) => void}
        onClose={onClose}
        onOpen={onOpen}
        preventKeyOpen={preventKeyOpen}
        preventOutsideClickUntilClosed={preventOutsideClickUntilClosed}
        renderEmptyState={renderEmptyState}
        renderFooter={renderFooter}
        renderGroup={(CpSelect as any).Group}
        renderItem={(CpSelect as any).ItemSingle}
        renderTrigger={
          renderTrigger ||
          ((triggerProps) => {
            const TriggerComponent = (CpSelect as any).TriggerSingle;
            return (
              <TriggerComponent
                {...triggerProps}
                placeholder={placeholder}
                onBlur={triggerProps.triggerOnBlur}
                title={title}
                error={error}
              />
            );
          })
        }
        searchFilter={defaultSearchFilter(searchFilter, searchValue)}
        searchOnChange={searchOnChange}
        searchPlaceholder={searchPlaceholder}
        searchValue={searchValue}
        stopPropagation={stopPropagation}
        style={style}
        transformData={transformData}
        triggerIsBlock={triggerIsBlock}
        triggerRef={triggerRef}
        value={value as TValue | TValue[]}
      />
      {typeof error === "string" && (
        <div className="cp-select--error-text">{error}</div>
      )}
    </Scoped>
  );
}

// Export type that includes static method
export type CpSelectSingleComponent = (<TData, TValue = TData>(
  props: CpSelectSingleProps<TData, TValue>,
) => React.JSX.Element) & {
  filterAlpha: typeof filterAlpha;
};

// Create typed component with static properties
const CpSelectSingle = CpSelectSingleBase as CpSelectSingleComponent;

// Attach static method
CpSelectSingle.filterAlpha = filterAlpha;

// Export the properly typed component
export { CpSelectSingle };
